import {
  arrow as arrowMiddleware,
  autoUpdate,
  type Boundary,
  type DetectOverflowOptions,
  flip,
  hide,
  limitShift,
  offset,
  type Placement,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom'
import { Slot } from '@radix-ui/react-slot'
import React, { forwardRef, useContext, useLayoutEffect, useMemo } from 'react'

import { arrowAlignment } from './arrow_alignment_middleware'
import { ArrowContext, PopperContext } from './context'
import type { Align, Side } from './types'

interface PopperContentProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  align?: Align;
  alignOffset?: number;
  arrowAlign?: Align;
  arrowPadding?: number;
  avoidCollisions?: boolean;
  boundary?: Boundary;
  children?: React.ReactNode;
  collisionPadding?: number;
  hideWhenDetached?: boolean;
  positionStrategy?: 'fixed' | 'absolute';
  pointerEvents?: 'auto' | 'none';
  shift?: boolean;
  side?: Side;
  sideOffset?: number;
  zIndex?: number;
}

function getPlacement (side: Side, align?: Align) {
  return side + (align !== 'center' ? '-' + align : '') as Placement
}

export const PopperContent = forwardRef<HTMLDivElement, PopperContentProps>(({
  asChild,
  align: alignProp = 'center',
  alignOffset,
  arrowAlign = 'center',
  arrowPadding = 0,
  avoidCollisions = true,
  boundary = 'clippingAncestors',
  children,
  collisionPadding,
  hideWhenDetached = true,
  positionStrategy = 'absolute',
  pointerEvents,
  shift: shiftEnabled = true,
  side: sideProp = 'bottom',
  sideOffset,
  zIndex,
  ...props
}, forwardedRef) => {
  const { anchor, arrow, content, setContent, setPlacement } =
    useContext(PopperContext)

  const overflowOptions: DetectOverflowOptions = {
    boundary,
    padding: collisionPadding,
  }

  const { floatingStyles, isPositioned, middlewareData, placement } =
    useFloating({
      elements: {
        floating: content,
        reference: anchor,
      },
      middleware: [
        offset({
          mainAxis: sideOffset,
          alignmentAxis: alignOffset,
        }),
        avoidCollisions &&
          shiftEnabled &&
          shift({
            ...overflowOptions,
            mainAxis: true,
            crossAxis: false,
            limiter: limitShift(),
          }),
        avoidCollisions &&
          flip({
            ...overflowOptions,
            mainAxis: true,
            crossAxis: false,
            fallbackStrategy: 'initialPlacement',
          }),
        arrow &&
          arrowMiddleware({
            element: arrow,
            padding: arrowPadding,
          }),
        arrow &&
          arrowAlignment({
            align: arrowAlign,
            element: arrow,
            padding: arrowPadding,
          }),
        size({
          ...overflowOptions,
          apply: (state) => {
            const { elements, rects, availableWidth, availableHeight } = state
            const { width: anchorWidth, height: anchorHeight } =
              rects.reference
            const contentStyle = elements.floating.style

            contentStyle.setProperty(
              '--popper-available-width',
              `${availableWidth}px`
            )
            contentStyle.setProperty(
              '--popper-available-height',
              `${availableHeight}px`
            )
            contentStyle.setProperty(
              '--popper-anchor-width',
              `${anchorWidth}px`
            )
            contentStyle.setProperty(
              '--popper-anchor-height',
              `${anchorHeight}px`
            )
          },
        }),
        hideWhenDetached && hide({ strategy: 'escaped', ...overflowOptions }),
      ],
      placement: getPlacement(sideProp, alignProp),
      strategy: positionStrategy,
      whileElementsMounted: autoUpdate,
    })

  const [side, align = 'center'] = placement.split('-') as [Side, Align | undefined]

  useLayoutEffect(() => {
    setPlacement(placement)
  }, [placement])

  const Component = asChild ? Slot : 'div'
  const arrowData = useMemo(() => {
    const { x, y } = middlewareData.arrowAlignment || {}

    return {
      side,
      x,
      y
    }
  }, [middlewareData.arrowAlignment, side])

  return (
    <div
      data-popper-content=''
      dir='ltr'
      ref={setContent}
      style={{
        ...floatingStyles,
        minWidth: 'max-content',
        opacity: !isPositioned ? 0 : undefined,
        pointerEvents: middlewareData.hide?.referenceHidden ? 'none' : pointerEvents,
        visibility: middlewareData.hide?.referenceHidden ? 'hidden' : undefined,
        zIndex,
      }}
    >
      <ArrowContext.Provider value={arrowData}>
        <Component
          {...props}
          data-align={align}
          data-side={side}
          ref={forwardedRef}
          style={{
          // Prevent animations when content hasn't been positioned yet
            animation: !isPositioned ? 'none' : undefined,
          }}
        >
          {children}
        </Component>
      </ArrowContext.Provider>
    </div>
  )
})
